/**
 * MailIntelSettingsContent — mail intelligence part of the Account Settings
 * "Mail" tab.
 *
 * Uses the member's own Outlook connection, made in Integrations: connecting
 * Outlook there starts mail triage for them, disconnecting stops it. This tab
 * only shows the state; there is no second place to connect and no second
 * mail sign-in. Also asks the weekly gate audit question,
 * "was this project mail?", over a few mails the relevance gate set aside.
 * Those answers are how gate recall is measured. Subject and preview come
 * live from Microsoft for the owner only; the backend keeps ids, not content.
 */
import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { FiCheck, FiInbox, FiMail, FiX } from 'react-icons/fi';

import { DataState } from '../../context/DataProvider';
import * as mailIntelApi from '../../services/mailIntelApi';

const DECISION_LABELS = {
  AUTO_EXCLUDE: 'Set aside',
  UNCERTAIN: 'Held, unsure',
};

const errorMessage = (err, fallback) =>
  (typeof err === 'string' ? err : err?.message) || fallback;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '—';

function AuditSample({ sample, onAnswer, busy }) {
  return (
    <li className="space-y-1.5 px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[12px] font-semibold text-zinc-100">
            {sample.subject || '(no subject)'}
          </p>
          <p className="truncate text-[11px] text-zinc-400">
            {sample.from?.name || sample.from?.email || 'Unknown sender'}
            {' · '}
            {formatDate(sample.received_at)}
          </p>
        </div>
        <span className="flex-shrink-0 rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-zinc-400">
          {DECISION_LABELS[sample.decision] || sample.decision}
        </span>
      </div>
      {sample.preview && (
        <p className="line-clamp-3 text-[11px] leading-4 text-zinc-500">
          {sample.preview}
        </p>
      )}
      <div className="flex gap-2 pt-0.5">
        <button
          onClick={() => onAnswer(sample, true)}
          disabled={busy}
          className="flex items-center gap-1 rounded-md border border-emerald-700/50 px-2 py-1 text-[11px] font-medium text-emerald-300 transition-colors hover:bg-emerald-900/30 disabled:opacity-40"
        >
          <FiCheck className="h-3 w-3" />
          Project mail
        </button>
        <button
          onClick={() => onAnswer(sample, false)}
          disabled={busy}
          className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[11px] font-medium text-zinc-300 transition-colors hover:bg-zinc-800 disabled:opacity-40"
        >
          <FiX className="h-3 w-3" />
          Not project mail
        </button>
      </div>
    </li>
  );
}

export default function MailIntelSettingsContent() {
  const { userGoogleId: accessToken } = DataState();
  const [mailbox, setMailbox] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [samples, setSamples] = useState([]);
  const [samplesLoading, setSamplesLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadMailbox = useCallback(async () => {
    try {
      const res = await mailIntelApi.getMailbox(accessToken);
      setMailbox(res?.data || { connected: false });
      setLoadError(null);
    } catch (err) {
      setLoadError(errorMessage(err, 'Could not load your mailbox status.'));
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadMailbox();
  }, [loadMailbox]);

  const isActive = mailbox?.connected && mailbox.status === 'active';

  useEffect(() => {
    if (!isActive) {
      setSamples([]);
      return undefined;
    }
    let cancelled = false;
    setSamplesLoading(true);
    mailIntelApi
      .listAuditSamples(accessToken)
      .then((res) => !cancelled && setSamples(res?.data?.samples || []))
      .catch((err) =>
        toast.error(errorMessage(err, 'Could not load mails to review.')),
      )
      .finally(() => !cancelled && setSamplesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [accessToken, isActive]);

  const handleAnswer = async (sample, projectMail) => {
    setBusyId(sample.id);
    try {
      await mailIntelApi.answerAuditSample(accessToken, sample.id, projectMail);
      setSamples((prev) => prev.filter((s) => s.id !== sample.id));
    } catch (err) {
      toast.error(errorMessage(err, 'Could not save your answer.'));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="p-1">
      <div className="mb-4">
        <h3 className="flex items-center gap-2 text-base font-bold text-zinc-100">
          <FiMail className="h-4 w-4 text-[#5652CF]" />
          Mail intelligence
        </h3>
        <p className="text-[10px] text-zinc-500">
          Reads your Inbox and Sent mail to track project threads. A filter sets
          aside HR, personal and broadcast mail before anything is analysed; for
          those only ids are kept. Internal preview: nothing is shown elsewhere
          in the app yet.
        </p>
      </div>

      {isLoading ? (
        <p className="text-[11px] text-zinc-500">Loading…</p>
      ) : loadError ? (
        <p className="rounded-md border border-dashed border-zinc-800 px-3 py-4 text-center text-[11px] text-zinc-500">
          {loadError}
        </p>
      ) : !mailbox?.connected ? (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/60 p-3">
          <p className="text-[12px] text-zinc-300">
            Your Outlook is not connected. Connect it in Integrations: the same
            connection is used by chat and by mail intelligence, and only by
            you.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="rounded-md border border-zinc-800 bg-zinc-900/60 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-0.5">
                <p className="truncate text-[12px] font-semibold text-zinc-100">
                  {mailbox.address}
                </p>
                <p className="text-[11px] text-zinc-400">
                  Last sync: {formatDate(mailbox.last_sync_at)}
                  {mailbox.folders?.length > 0 &&
                    ` · ${mailbox.folders.join(', ')}`}
                </p>
                {!isActive && (
                  <p className="text-[11px] text-amber-400">
                    {mailbox.status}
                    {mailbox.status_reason ? `: ${mailbox.status_reason}` : ''}.
                    Reconnect Outlook in Integrations to resume.
                  </p>
                )}
              </div>
              <span
                className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${
                  isActive
                    ? 'bg-emerald-900/40 text-emerald-300'
                    : 'bg-amber-900/40 text-amber-300'
                }`}
              >
                {mailbox.status}
              </span>
            </div>
          </div>

          {isActive && (
            <div className="space-y-1.5">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                Weekly check: was this project mail?
              </h4>
              <p className="text-[10px] text-zinc-500">
                A few mails the filter set aside this week. Your answers tell us
                how often it drops real project mail.
              </p>
              {samplesLoading ? (
                <p className="text-[11px] text-zinc-500">Loading…</p>
              ) : samples.length === 0 ? (
                <p className="flex items-center justify-center gap-2 rounded-md border border-dashed border-zinc-800 px-3 py-4 text-[11px] text-zinc-500">
                  <FiInbox className="h-3.5 w-3.5" />
                  Nothing to review right now.
                </p>
              ) : (
                <ul className="divide-y divide-zinc-800 rounded-md border border-zinc-800 bg-zinc-900/60">
                  {samples.map((s) => (
                    <AuditSample
                      key={s.id}
                      sample={s}
                      onAnswer={handleAnswer}
                      busy={busyId === s.id}
                    />
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
